import { IconWifiOff } from "@tabler/icons-react"
import { onlineManager, useQueryClient } from "@tanstack/react-query"
import { useCallback, useState, useSyncExternalStore } from "react"

import Button from "~/components/Button"
import { getConnectionFailed, isConnectionError, subscribeConnection } from "~/utilities/connectivity"

const subscribeOnline = (listener: () => void) => {
	window.addEventListener("online", listener)
	window.addEventListener("offline", listener)
	const unsubscribe = onlineManager.subscribe(listener)
	return () => {
		window.removeEventListener("online", listener)
		window.removeEventListener("offline", listener)
		unsubscribe()
	}
}
const getOffline = () => !navigator.onLine || !onlineManager.isOnline()

export default function ConnectionBanner() {
	const client = useQueryClient()
	const cache = client.getQueryCache()
	const offline = useSyncExternalStore(subscribeOnline, getOffline)
	const transportFailed = useSyncExternalStore(subscribeConnection, getConnectionFailed)
	const subscribeQueries = useCallback((listener: () => void) => cache.subscribe(listener), [cache])
	const getQueryProblem = useCallback(
		() =>
			cache
				.getAll()
				.some(
					query =>
						query.isActive() &&
						(query.state.fetchStatus === "paused" ||
							isConnectionError(query.state.error) ||
							isConnectionError(query.state.fetchFailureReason)),
				),
		[cache],
	)
	const queryProblem = useSyncExternalStore(subscribeQueries, getQueryProblem)
	const [retrying, setRetrying] = useState(false)

	if (!offline && !transportFailed && !queryProblem) return null

	async function retry() {
		setRetrying(true)
		try {
			// Retry reads only. Never replay a save whose server-side outcome is unknown.
			const needsRetry = (query: ReturnType<typeof cache.getAll>[number]) =>
				query.state.status === "error" || query.state.fetchStatus === "paused" || query.state.data === undefined
			const hasFailedReads = cache.getAll().some(query => query.isActive() && needsRetry(query))
			await client.refetchQueries({
				type: "active",
				predicate: query => query.state.fetchStatus !== "fetching" && (!hasFailedReads || needsRetry(query)),
			})
		} finally {
			setRetrying(false)
		}
	}

	return (
		<div
			role="status"
			aria-live="polite"
			className="border-component-border fixed inset-x-3 top-3 z-[10000] mx-auto flex max-w-xl items-center gap-3 rounded-xl border bg-white p-4 shadow-lg"
		>
			<IconWifiOff className="text-light size-5 shrink-0" aria-hidden />
			<div className="min-w-0 flex-1 text-sm">
				<p className="font-semibold">
					{offline ? "You appear to be offline" : "We're having trouble connecting"}
				</p>
				<p className="text-light mt-1">
					{offline
						? "We'll retry loading when your connection returns."
						: "We couldn't reach Forento. Check your connection and try again."}
				</p>
			</div>
			<Button variant="secondary" onClick={retry} isLoading={retrying}>
				Retry
			</Button>
		</div>
	)
}
